import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
}

// TYPES FOR CUSTOM COLOR "neutral" + semantic SURFACE tokens
type SurfaceToken = { bg: string; text: string };
type SurfacePalette = {
  info: SurfaceToken;
  warning: SurfaceToken;
  critical: SurfaceToken;
  success: SurfaceToken;
};
declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
    surface: SurfacePalette;
  }
  interface PaletteOptions {
    neutral: PaletteOptions['primary'];
    surface: SurfacePalette;
  }
}

// COMPONENT PROPS TYPES OVERRIDES TO SUPPORT COLOR "neutral"
declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    neutral: true;
  }
}

// SETUP COLORS

export const grey = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#2f3941',
  900: '#161C24'
};

// NEW BRAND: Zenjob Blue. main + light(hover) from Figma; lighter/dark/darker derived
// from #2D31FF until design confirms the full ramp (see docs/rebrand-tokens.md).
export const primary = {
  lighter: '#EBECFF',
  light: '#97B3FF',
  main: '#2D31FF',
  dark: '#1D20B6',
  darker: '#14157A',
  contrastText: '#FFFFFF'
};

// Secondary = dark ink (per product decision) so blue stays the single brand accent.
export const secondary = {
  lighter: '#DFE3E8',
  light: '#637381',
  main: '#212B36',
  dark: '#161C24',
  darker: '#0B0F19',
  contrastText: '#FFFFFF'
};

export const info = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#FFFFFF'
};

export const success = {
  lighter: '#D3FCD2',
  light: '#77ED8B',
  main: '#22C55E',
  dark: '#118D57',
  darker: '#065E49',
  contrastText: '#ffffff'
};

export const warning = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: grey[800]
};

export const error = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#FF5630',
  dark: '#B71D18',
  darker: '#7A0916',
  contrastText: '#FFFFFF'
};

// Figma semantic SURFACES — solid notification/status backgrounds + on-surface text.
// One source of truth for message cards (InfoCard/WarningCard) and the solid status pills
// (in-review, active/closed). See docs/rebrand-tokens.md (surface.info/success/warning).
export const surface = {
  info: { bg: '#EBF3FF', text: '#1D20B6' },
  warning: { bg: '#FFF4ED', text: '#C61C08' },
  critical: { bg: '#FFF1F3', text: '#C80D2C' },
  success: { bg: '#EFFEF4', text: '#11843D' }
} as const;

// CUSTOM COLOR "neutral"
export const neutral = {
  lighter: '#F4F6F8',
  light: '#DFE3E8',
  main: '#919EAB',
  dark: '#454F5B',
  darker: '#212B36',
  contrastText: '#FFFFFF'
};

export const common = {
  black: '#000000',
  white: '#FFFFFF'
};

export const action = {
  hover: alpha(grey[500], 0.08),
  selected: alpha(grey[500], 0.16),
  disabled: alpha(grey[500], 0.8),
  disabledBackground: alpha(grey[500], 0.24),
  focus: alpha(grey[500], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48
};

const base = {
  primary,
  secondary,
  neutral,
  info,
  success,
  warning,
  error,
  surface,
  grey,
  common,
  divider: alpha(grey[500], 0.2),
  action
};

// ----------------------------------------------------------------------

export function palette(mode: 'light' | 'dark') {
  const light = {
    ...base,
    mode: 'light',
    text: {
      // Rebrand: body/secondary copy is ink too ("font color always black"); the gray
      // `disabled` is kept only for genuine disabled affordances (placeholders, etc.).
      primary: '#212B36',
      secondary: '#212B36',
      disabled: grey[500]
    },
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF',
      neutral: grey[200]
    },
    action: {
      ...base.action,
      active: grey[600]
    }
  };

  const dark = {
    ...base,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: grey[500],
      disabled: grey[600]
    },
    background: {
      paper: grey[800],
      default: grey[900],
      neutral: alpha(grey[500], 0.12)
    },
    action: {
      ...base.action,
      active: grey[500]
    }
  };

  return mode === 'light' ? light : dark;
}
